import { useMemo } from 'react';

import type { TaskView, WorkflowView } from '@/lib/api/generated/model';
import {
  TASK_STATE_DOT,
  SplitSelectionControl,
  TaskCostFigure,
  listWorkspaceTasks,
  taskStateWords,
  useSubtaskSelection,
  type WorkspaceTaskRow,
} from '@/features/tasks';
import { useCanWork } from '@/features/workspaces/canWork';
import { PhoneCard, PhoneCardList } from '@/components/ui/phone/PhoneCardList';
import { usePhoneWidth } from '@/shared/hooks/usePhoneWidth';
import { formatExactTime, formatRelativeTime } from '@/shared/utils/time';

export interface RunTaskListProps {
  targetWorkflow: WorkflowView;
  tasks: TaskView[];
  onOpenTask?: (taskId: string) => void;
  /**
   * Present while the parent waits on this run: rows get a checkbox and the
   * ticked ones can be moved into a new task (spec 137 US3).
   */
  split?: { workspaceId: string; parent: { id: string; title: string } };
}

/** A row's state in the board's own words — "Done" once it reaches the done column. */
function StateText({ row }: { row: WorkspaceTaskRow }) {
  // A reader is told a gate waits on a member, not on them (spec 193).
  const words = taskStateWords(useCanWork());
  if (row.finished) {
    return (
      <span className="flex items-center gap-1.5">
        <span
          aria-hidden="true"
          className="bg-board-state-done size-2 shrink-0 rounded-full"
        />
        Done
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1.5">
      <span
        aria-hidden="true"
        className={`${TASK_STATE_DOT[row.state]} size-2 shrink-0 rounded-full`}
      />
      {words[row.state].one}
    </span>
  );
}

/**
 * The run's created tasks as rows, for a batch too long to scan as cards.
 *
 * Rows come from `listWorkspaceTasks` — the Tasks page's own derivation — so
 * a task reads the same state here as on its card and on that page. The Tasks
 * page's row and cell components stay internal to `workspace-tasks` (its
 * index says why); this is a deliberately smaller table of its own.
 *
 * A row opens its task through `onOpenTask`, the same `?task=` drawer the
 * board's cards open.
 *
 * With `split`, rows also get a checkbox and the ticked ones can be moved into
 * a new task (spec 137 US3), the same control the drawer's list uses.
 */
export function RunTaskList({
  targetWorkflow,
  tasks,
  onOpenTask,
  split,
}: RunTaskListProps) {
  const rows = useMemo(
    () =>
      listWorkspaceTasks([targetWorkflow], tasks).sort((a, b) =>
        a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : 0,
      ),
    [targetWorkflow, tasks],
  );

  const { selected, toggle, clear } = useSubtaskSelection(
    rows.map((row) => row.taskId),
  );
  const isPhone = usePhoneWidth();

  if (rows.length === 0) {
    return (
      <p className="text-muted-foreground p-6 text-center text-sm">
        This run has not created any tasks.
      </p>
    );
  }

  return (
    <>
      {isPhone ? (
        <PhoneCardList label="Tasks this run created">
          {rows.map((row) => (
            <PhoneCard
              key={row.taskId}
              title={row.title}
              onOpen={onOpenTask ? () => onOpenTask(row.taskId) : undefined}
              actions={
                split ? (
                  <label className="flex size-11 items-center justify-center">
                    <input
                      type="checkbox"
                      aria-label={`Select ${row.title}`}
                      className="accent-primary size-5"
                      checked={selected.has(row.taskId)}
                      onChange={() => toggle(row.taskId)}
                    />
                  </label>
                ) : undefined
              }
              lines={[
                <span key="where" className="flex flex-wrap items-center gap-2">
                  {row.stepLabel ?? 'Out of place'} ·
                  <StateText row={row} />
                  {row.needsAttention ? (
                    <span className="bg-board-state-failed/12 text-board-state-failed rounded-sm px-1.5 text-xs leading-4 font-medium">
                      Attention
                    </span>
                  ) : null}
                  {row.needsReview ? (
                    <span className="bg-board-state-waiting/12 text-board-state-waiting rounded-sm px-1.5 text-xs leading-4 font-medium">
                      Review
                    </span>
                  ) : null}
                </span>,
                <span key="when" className="flex items-center gap-1">
                  <TaskCostFigure cost={row.cost} placeholder="—" /> ·
                  <span title={formatExactTime(row.updatedAt)}>
                    {formatRelativeTime(row.updatedAt)}
                  </span>
                </span>,
              ]}
            />
          ))}
        </PhoneCardList>
      ) : (
        <div className="border-border overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <caption className="sr-only">Tasks this run created</caption>
            <thead className="text-muted-foreground text-left text-xs">
              <tr className="border-border border-b">
                {split ? (
                  <th scope="col" className="w-8 px-3 py-2">
                    <span className="sr-only">Select</span>
                  </th>
                ) : null}
                <th scope="col" className="px-3 py-2 font-medium">
                  Task
                </th>
                <th scope="col" className="px-3 py-2 font-medium">
                  Step
                </th>
                <th scope="col" className="px-3 py-2 font-medium">
                  State
                </th>
                <th scope="col" className="px-3 py-2 text-right font-medium">
                  Cost
                </th>
                <th scope="col" className="px-3 py-2 text-right font-medium">
                  Updated
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.taskId}
                  className="border-border hover:bg-muted/50 border-b last:border-b-0"
                >
                  {split ? (
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        aria-label={`Select ${row.title}`}
                        className="accent-primary size-4"
                        checked={selected.has(row.taskId)}
                        onChange={() => toggle(row.taskId)}
                      />
                    </td>
                  ) : null}
                  <td className="max-w-80 px-3 py-2">
                    <button
                      type="button"
                      onClick={() => onOpenTask?.(row.taskId)}
                      className="text-foreground focus-visible:ring-ring block max-w-full truncate rounded-sm text-left font-medium hover:underline focus-visible:ring-3 focus-visible:outline-none"
                      title={row.title}
                    >
                      {row.title}
                    </button>
                  </td>
                  <td className="text-muted-foreground px-3 py-2 whitespace-nowrap">
                    {row.stepLabel ?? 'Out of place'}
                  </td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">
                    <span className="flex items-center gap-2">
                      <StateText row={row} />
                      {row.needsAttention ? (
                        <span className="bg-board-state-failed/12 text-board-state-failed rounded-sm px-1.5 leading-4 font-medium">
                          Attention
                        </span>
                      ) : null}
                      {row.needsReview ? (
                        <span className="bg-board-state-waiting/12 text-board-state-waiting rounded-sm px-1.5 leading-4 font-medium">
                          Review
                        </span>
                      ) : null}
                    </span>
                  </td>
                  <td className="text-muted-foreground px-3 py-2 text-right text-xs">
                    <TaskCostFigure cost={row.cost} placeholder="—" />
                  </td>
                  <td
                    className="text-muted-foreground px-3 py-2 text-right text-xs whitespace-nowrap"
                    title={formatExactTime(row.updatedAt)}
                  >
                    {formatRelativeTime(row.updatedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {split ? (
        <SplitSelectionControl
          workspaceId={split.workspaceId}
          parent={split.parent}
          selectedIds={[...selected]}
          total={rows.length}
          onSplit={clear}
        />
      ) : null}
    </>
  );
}
